import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "antd";
import moment from "moment";

import ExternalTransactionForm from "./ExternalTransactionForm";
import {
  Asset,
  ILocalAsset,
  ITransaction,
  Direction,
  BusinessAssetForm,
} from "../../../../types";
import { createTransactionAsset } from "../../../common/DragAndDrop/actionHandler";
import { useUpsertExternalTransactionMutation } from "../../../../api/graphql";
import api from "../../../../api";
import { OcrReceiptFields } from "../../../../api/modules/KontaxApi";
import {
  showErrorNotification,
  showGraphQlErrorNotification,
} from "../../../../utils/notifications";
import { reportError } from "../../../../sentry";
import { isDepreciableCategoryCode } from "../../../BusinessAsset/utils";
import {
  disabledDateAfterToday,
  getBusinessAssetFormForUpdate,
  getBusinessAssetForm,
} from "./utils";
import { BusinessAsset } from "../../../../api/graphql/schema.generated";
import {
  recipientVatIdForUpsert,
  showsRecipientVatIdField,
} from "../../TransactionView/helpers";

type Values = {
  amount: number;
  paymentDate: string;
  categoryCode: string;
  vatCategoryCode: string;
  recipientVatId?: string;
  name?: string;
  iban?: string;
  description?: string;
  assets?: Array<Asset>;
  direction?: Direction;
  businessAssetForm?: BusinessAssetForm;
};

export interface ExternalTransactionInitialValues {
  id?: string;
  amount?: number;
  assets?: Array<Asset>;
  valutaDate?: string;
  categoryCode?: string | null;
  name?: string | null;
  iban?: string | null;
  description?: string | null;
  vatCategoryCode?: string | null;
  recipientVatId?: string | null;
  businessAssets?: BusinessAsset[];
}

type CreateFileResult =
  | { asset: Asset; file: File }
  | { asset: Asset; file: null; error: string };

// Only used to re-download an already-linked email document's asset when a
// Belege transaction gets duplicated (SMT-1346): the preview itself renders
// straight from `asset.fullsize`, never from this. The download can fail on
// its own, independently of everything else on the form: the
// transaction-assets bucket only allows the WebApp origin, so a fetch from
// the Backoffice origin is rejected by CORS; the presigned URL can also have
// expired, or the S3 object can have been deleted. Every fetch is isolated
// here so a single failure surfaces as a sentinel instead of an unhandled
// rejection in the caller's Promise.all.
async function createFile(asset: Asset): Promise<CreateFileResult> {
  try {
    const response = await fetch(asset.fullsize);
    if (!response.ok) {
      throw new Error(`Unexpected response status: ${response.status}`);
    }
    const data = await response.blob();
    const metadata = {
      type: asset.filetype,
    };
    const file = new File(
      [data],
      asset.id || new Date().getTime().toString(),
      metadata
    );
    return { asset, file };
  } catch (error) {
    return {
      asset,
      file: null,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

// Surfaces the per-asset download failure to Sentry (instead of letting it
// die as an unhandled promise rejection) so the "why do these URLs fail"
// follow-up can be answered from real data: asset id + reason, once per
// asset.
function reportAssetDownloadFailure(asset: Asset, reason: string) {
  reportError(new Error(`Failed to fetch transaction asset: ${reason}`), {
    tags: { assetId: asset.id || "unknown" },
    extra: { assetFullsize: asset.fullsize, reason },
  });
}

// Surfaces a per-asset upload failure to Sentry, same reasoning as
// reportAssetDownloadFailure but for the re-upload half of the duplicate
// flow: the file was downloaded fine, but creating the asset on the new
// transaction (S3 upload or acknowledgement) failed.
function reportAssetUploadFailure(newTransactionId: string, reason: string) {
  reportError(new Error(`Failed to upload transaction asset copy: ${reason}`), {
    tags: { transactionId: newTransactionId },
    extra: { reason },
  });
}

// Downloads every asset of a duplicated Belege transaction so it can carry
// its own copy of the linked email document's asset, instead of sharing it
// with the original transaction. A failed download must not block the
// duplicate from being created: it is reported once per asset and the
// caller decides how to surface it to the operator.
async function downloadAssetsForDuplicate(
  assets: Asset[]
): Promise<{ files: File[]; failed: Asset[] }> {
  const results = await Promise.all(assets.map(createFile));
  const files: File[] = [];
  const failed: Asset[] = [];

  results.forEach((result) => {
    if (result.file) {
      files.push(result.file);
    } else {
      failed.push(result.asset);
      reportAssetDownloadFailure(result.asset, result.error);
    }
  });

  return { files, failed };
}

export const ExternalTransactionPopup = ({
  onSuccess,
  isPopupShown,
  email,
  closePopup,
  initialValues,
  emailDocumentId,
  hideAssetActions,
}: {
  isPopupShown: boolean;
  onSuccess: Function;
  email: string;
  closePopup: Function;
  initialValues?: ExternalTransactionInitialValues;
  emailDocumentId?: string;
  hideAssetActions?: boolean;
}) => {
  const [form] = Form.useForm();
  const [assets, setAssets] = useState<Asset[]>(initialValues?.assets || []);
  // Only ever holds files the operator dropped via `onDropFiles`: the
  // preview renders straight from `asset.fullsize`, so nothing is
  // downloaded into this just to display it (SMT-1346).
  const [files, setFiles] = useState<File[]>([]);
  const [isReplacingAsset, setIsReplacingAsset] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDuplicatedTransaction, setIsDuplicatedTransaction] = useState(false);
  const [isOcrRunning, setIsOcrRunning] = useState(false);

  const [upsertExternalTransaction] = useUpsertExternalTransactionMutation();

  // OCR pre-fill only applies to the "+ Add transaction" flow (Verification
  // View): the edit flow has initialValues, and the Belege View flow is already
  // pre-filled server-side via the email document.
  const isAddFlow = !initialValues && !emailDocumentId;

  const prefillEmptyFields = (fields: OcrReceiptFields) => {
    const isEmptyField = (name: string) => {
      const value = form.getFieldValue(name);
      return value === undefined || value === null || value === "";
    };

    const updates: Record<string, unknown> = {};
    // amount is returned in cents; the form holds it as a positive euro value.
    if (fields.amount != null && isEmptyField("amount")) {
      updates.amount = Math.abs(fields.amount) / 100;
    }
    if (fields.date && isEmptyField("paymentDate")) {
      const paymentDate = moment(fields.date);
      if (!disabledDateAfterToday(paymentDate)) {
        updates.paymentDate = paymentDate;
      }
    }
    if (fields.name && isEmptyField("name")) {
      updates.name = fields.name;
    }
    if (fields.documentNumber && isEmptyField("description")) {
      updates.description = fields.documentNumber;
    }

    if (Object.keys(updates).length) {
      form.setFieldsValue(updates);
    }
  };

  const runOcrPrefill = async (file: File) => {
    setIsOcrRunning(true);
    try {
      const fields = await api.kontax.ocrTransactionReceipt(email, file);
      prefillEmptyFields(fields);
    } catch {
      // OCR is best-effort and must never block manual entry.
    } finally {
      setIsOcrRunning(false);
    }
  };

  const upsertTransaction = async (values: Values, transactionId?: string) => {
    const isDocumentMatch = !!emailDocumentId;

    const amountDirection = parseInt(
      `${values.direction || Direction.OUTGOING}`,
      10
    );

    const transactionPayload = {
      id: transactionId,
      ...values,
      amount: amountDirection * Math.round(values.amount * 100),
      paymentDate: moment(values.paymentDate).format("YYYY-MM-DD"),
      businessAssetForm: getBusinessAssetFormForUpdate(
        values.businessAssetForm
      ),
      recipientVatId: recipientVatIdForUpsert(
        values.vatCategoryCode,
        values.recipientVatId
      ),
    };
    delete transactionPayload.assets;
    delete transactionPayload.direction;
    const result = await upsertExternalTransaction({
      variables: {
        email,
        transaction: transactionPayload,
        options:
          // if in BelegeView a virtual transaction got duplicated, it should use previous
          // asset and should not trigger any document match logic
          !isDuplicatedTransaction && isDocumentMatch
            ? { emailDocumentId }
            : {},
      },
    });

    const newTransactionId = result?.data?.upsertExternalTransaction
      .id as string;

    if (isDocumentMatch) {
      if (isDuplicatedTransaction) {
        // The duplicate doesn't share the original transaction's linked
        // email document, so it needs its own copy of that asset: download
        // it here, lazily, only for this path, and re-upload it to the new
        // transaction. Neither a failed download nor a failed upload must
        // block the duplicate itself from being created, or make it look
        // like the transaction creation failed: allSettled so one rejected
        // upload can't reach handleSubmit's catch and skip onSuccess.
        const { files: downloaded, failed: failedDownloads } =
          await downloadAssetsForDuplicate(assets);
        const uploadResults = await Promise.allSettled(
          downloaded.map((file) =>
            createTransactionAsset(newTransactionId, file)
          )
        );
        const rejectedUploads = uploadResults.filter(
          (result): result is PromiseRejectedResult =>
            result.status === "rejected"
        );
        rejectedUploads.forEach((result) => {
          const reason =
            result.reason instanceof Error
              ? result.reason.message
              : String(result.reason);
          reportAssetUploadFailure(newTransactionId, reason);
        });
        const hasFailedUpload = rejectedUploads.length > 0;
        if (failedDownloads.length || hasFailedUpload) {
          showErrorNotification({
            message:
              "The receipt could not be attached to the duplicated transaction.",
          });
        }
      }
      // else: first submission of an email document match - the backend
      // links the asset on its own side, no upload required.
    } else {
      // Not a document match: upload only what the operator dropped.
      await Promise.all(
        files.map((file) => createTransactionAsset(newTransactionId, file))
      );
    }

    return result.data?.upsertExternalTransaction as ITransaction;
  };

  useEffect(() => {
    setAssets(initialValues?.assets || []);
  }, [initialValues?.assets]);

  useEffect(() => {
    setIsModalVisible(isPopupShown);
  }, [isPopupShown]);

  useEffect(() => {
    if (!initialValues) {
      form.resetFields();
      form.setFields([
        {
          name: "direction",
          value: Direction.OUTGOING,
        },
      ]);
    } else {
      form.setFields([
        {
          name: "amount",
          value: initialValues.amount
            ? Math.abs(initialValues.amount / 100)
            : "",
        },
        {
          name: "direction",
          value: getInitialDirection(initialValues),
        },
        {
          name: "paymentDate",
          value: moment(initialValues.valutaDate),
        },
        { name: "assets", value: initialValues.assets },
        { name: "categoryCode", value: initialValues.categoryCode },
        { name: "name", value: initialValues.name },
        { name: "iban", value: initialValues.iban },
        { name: "description", value: initialValues.description },
        { name: "vatCategoryCode", value: initialValues.vatCategoryCode },
        {
          name: "recipientVatId",
          value: initialValues.recipientVatId ?? "",
        },
        {
          name: "businessAssetForm",
          value: getBusinessAssetForm(initialValues.businessAssets),
        },
      ]);
    }
  }, [initialValues, form]);
  const handleCancel = () => {
    closePopup();
    handleReset();
  };

  const handleSubmit = async (values: Values, isDuplicate = false) => {
    // Captured before handleReset() (which clears it back to false), so
    // onSuccess still learns whether this submit belonged to a transaction
    // that had already been duplicated earlier in the same popup session.
    const wasDuplicatedTransaction = isDuplicatedTransaction;
    setIsButtonLoading(true);
    try {
      const transaction = await upsertTransaction(values, initialValues?.id);
      setIsButtonLoading(false);
      if (!isDuplicate) {
        closePopup();
        handleReset();
      } else {
        setIsDuplicatedTransaction(true);
      }

      const assets =
        initialValues?.id && isReplacingAsset
          ? initialValues?.assets
          : values.assets;

      onSuccess(
        {
          ...transaction,
          assets: isReplacingAsset ? values.assets : assets,
        },
        isDuplicate,
        wasDuplicatedTransaction
      );
    } catch (error) {
      setIsButtonLoading(false);
      showGraphQlErrorNotification(
        `${initialValues?.id ? "Update" : "Create"} external transaction:`,
        error
      );
    }
  };

  const onDropFiles = (files: File[]) => {
    const newAssets = files.map((file) => ({
      fullsize: URL.createObjectURL(file),
      filetype: file.type.split("/").pop()!,
    }));
    const updatedAssets = assets ? assets.concat(newAssets) : newAssets;

    form.setFieldsValue({
      assets: updatedAssets,
    });
    // Needed to re-render the AssetsViewer when one or multiple assets are added
    setAssets(updatedAssets);
    setFiles(files);

    if (isAddFlow && files.length) {
      void runOcrPrefill(files[0]);
    }
  };

  const onChangeCategory = (categoryCode: string | null) => {
    form.setFieldsValue({ categoryCode });
    form.setFieldsValue({
      businessAssetForm: isDepreciableCategoryCode(categoryCode)
        ? {}
        : undefined,
    }); // set businessAssetForm to {} to reveal the form
    if (form.getFieldValue("vatCategoryCode")) {
      form.resetFields(["vatCategoryCode"]);
    }
    form.setFieldsValue({ recipientVatId: undefined });
  };

  const onChangeBusinessAsset = (businessAssetForm: BusinessAssetForm) => {
    form.setFieldsValue({ businessAssetForm });
  };

  const onVatCategorySelected = (category: string | null) => {
    form.setFieldsValue({
      vatCategoryCode: category,
    });
    if (!showsRecipientVatIdField(category)) {
      form.setFieldsValue({ recipientVatId: undefined });
    }
  };

  const handleReset = () => {
    setAssets([]);
    setIsReplacingAsset(false);
    setIsDuplicatedTransaction(false);
    form.resetFields();
    form.setFields([
      {
        name: "direction",
        value: Direction.OUTGOING,
      },
    ]);
  };

  const onAssetDelete = (deletedAsset: Asset | ILocalAsset) => {
    const deletedFullsize =
      "fullsize" in deletedAsset ? deletedAsset.fullsize : undefined;
    const newAssets = assets.filter(
      (asset) => asset !== deletedAsset || asset?.fullsize !== deletedFullsize
    );
    setAssets(newAssets);
    form.setFieldsValue({ assets: newAssets });
    setIsReplacingAsset(true);
  };

  const isEditMode = !!initialValues && !emailDocumentId;

  const handleDuplicate = async () => {
    try {
      await form.validateFields();
      const formValues = form.getFieldsValue();
      await handleSubmit(formValues, true);
      form.setFieldsValue({
        amount: null,
      });
    } catch (err: any) {
      if (!err.errorFields) {
        showErrorNotification({ message: err.message || "An error occurred" });
      }
    }
  };

  const footer = [
    <Button
      form="externalTransactionFormId"
      type="default"
      loading={isButtonLoading}
      onClick={handleDuplicate}
      data-test="duplicateButton"
    >
      {isEditMode ? "Duplicate" : "Add & Duplicate"}
    </Button>,
    <Button
      form="externalTransactionFormId"
      key="submit"
      htmlType="submit"
      type="primary"
      loading={isButtonLoading}
    >
      {isEditMode ? "Update" : "Create"}
    </Button>,
  ];

  return (
    <Modal
      data-test="externalTransactionPopup"
      title={`${isEditMode ? "Update" : "Add"} an external transaction`}
      visible={isModalVisible}
      onCancel={handleCancel}
      footer={footer}
      centered
      width={1058}
      bodyStyle={{
        paddingBottom: (form.getFieldValue("assets") || []).length > 1 ? 40 : 0,
      }}
    >
      <Form
        form={form}
        name="externalTransaction"
        id="externalTransactionFormId"
        onFinish={handleSubmit}
        autoComplete="off"
        layout="vertical"
        requiredMark="optional"
      >
        <ExternalTransactionForm
          onDropFiles={hideAssetActions ? undefined : onDropFiles}
          assets={assets}
          onChangeCategory={onChangeCategory}
          onVatCategorySelected={onVatCategorySelected}
          onAssetDelete={hideAssetActions ? undefined : onAssetDelete}
          onChangeBusinessAsset={onChangeBusinessAsset}
          isOcrLoading={isOcrRunning}
        />
      </Form>
    </Modal>
  );
};

export function getInitialDirection(
  initialValues: ExternalTransactionInitialValues
) {
  if (initialValues?.id && initialValues.amount) {
    return initialValues.amount > 0 ? Direction.INCOMING : Direction.OUTGOING;
  }
  return Direction.OUTGOING;
}
